import { useEffect, useState } from 'react';
import { ApiError, del, get, post, put } from '../api';
import { Avisos, Dist, Vacio } from '../components/ui';

interface Sociedad {
  companyDb: string;
  name: string;
  cif: string | null;
  cifSource: 'sap' | 'panel' | null;
  cifCheckedAt: string | null;
  cifSapRaw: string | null;
  cifRejected: string | null;
  slUsername: string;
  hasPassword: boolean;
  isDefault: boolean;
  active: boolean;
}

interface Cliente {
  id: string;
  slug: string;
  name: string;
  hubClient: string;
  slBaseUrl: string;
  slVerifySsl: boolean;
  slTimeoutMs: number;
  active: boolean;
  companies: Sociedad[];
}

interface Tunel {
  name: string;
  ip: string | null;
}

export function Admin(): JSX.Element {
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [tuneles, setTuneles] = useState<Tunel[]>([]);
  const [abierto, setAbierto] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nuevo, setNuevo] = useState(false);

  async function cargar(): Promise<void> {
    try {
      const [c, t] = await Promise.all([
        get<{ tenants: Cliente[] }>('/admin/tenants'),
        get<{ tunnels: Tunel[] }>('/admin/tunnels'),
      ]);
      setClientes(c.tenants);
      setTuneles(t.tunnels);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  useEffect(() => {
    void cargar();
  }, []);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <h1>Clientes</h1>
        <button className="primario" style={{ marginLeft: 'auto' }} onClick={() => setNuevo((v) => !v)}>
          {nuevo ? 'Cancelar' : 'Nuevo cliente'}
        </button>
      </div>

      {error ? <Avisos titulo="Error" items={[error]} error /> : null}

      {nuevo ? (
        <NuevoCliente
          tuneles={tuneles}
          onCreado={() => {
            setNuevo(false);
            void cargar();
          }}
        />
      ) : null}

      {clientes.length === 0 ? (
        <div className="tarjeta"><Vacio>Todavía no hay ningún cliente dado de alta.</Vacio></div>
      ) : (
        clientes.map((c) => (
          <FichaCliente
            key={c.id}
            cliente={c}
            tuneles={tuneles}
            abierto={abierto === c.slug}
            onAbrir={() => setAbierto(abierto === c.slug ? null : c.slug)}
            onCambio={() => void cargar()}
          />
        ))
      )}
    </div>
  );
}

function NuevoCliente({ tuneles, onCreado }: { tuneles: Tunel[]; onCreado: () => void }): JSX.Element {
  const [slug, setSlug] = useState('');
  const [nombre, setNombre] = useState('');
  const [tunel, setTunel] = useState(tuneles[0]?.name ?? '');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pista, setPista] = useState<string | null>(null);

  // Al elegir el túnel se propone su dirección de Service Layer con la IP de
  // la VPN. Es la que hay que usar: la IP de la LAN del cliente no contesta
  // porque el túnel termina en un Windows que reenvía con portproxy.
  useEffect(() => {
    const t = tuneles.find((x) => x.name === tunel);
    if (t?.ip && url === '') setUrl(`https://${t.ip}:50000/b1s/v1`);
  }, [tunel, tuneles, url]);

  async function crear(): Promise<void> {
    setError(null);
    setPista(null);
    try {
      await post('/admin/tenants', {
        slug,
        name: nombre,
        hubClient: tunel,
        slBaseUrl: url,
        slVerifySsl: false,
        slTimeoutMs: 30000,
        active: true,
      });
      onCreado();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
      setPista(e instanceof ApiError ? e.hint : null);
    }
  }

  return (
    <div className="tarjeta" style={{ marginBottom: 14 }}>
      <h2 style={{ marginBottom: 10 }}>Nuevo cliente</h2>
      <div className="rejilla dos">
        <label className="campo">
          Identificador (será su subdominio)
          <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="promael" />
          <span className="sutil">{slug || '…'}.mrp.ecinsapp.es</span>
        </label>
        <label className="campo">
          Nombre
          <input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Promael SL" />
        </label>
        <label className="campo">
          Túnel del hub
          <select value={tunel} onChange={(e) => setTunel(e.target.value)}>
            {tuneles.map((t) => (
              <option key={t.name} value={t.name}>
                {t.name}
                {t.ip ? ` — ${t.ip}` : ''}
              </option>
            ))}
          </select>
          <span className="sutil">
            El nombre viaja tal cual en X-Ecinsa-Cliente, con sus mayúsculas. Los túneles se dan de alta
            en el hub, no aquí.
          </span>
        </label>
        <label className="campo">
          Service Layer
          <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://10.30.0.5:50000/b1s/v1" />
          <span className="sutil">Por la IP del cliente dentro de la VPN (10.30.0.X), nunca la de su red local.</span>
        </label>
      </div>
      {error ? (
        <div className="aviso-caja error" style={{ marginTop: 10 }}>
          <div className="titulo">{error}</div>
          {pista ? <div className="sutil">{pista}</div> : null}
        </div>
      ) : null}
      <button className="primario" style={{ marginTop: 12 }} onClick={() => void crear()}>
        Crear cliente
      </button>
    </div>
  );
}

function FichaCliente({
  cliente,
  tuneles,
  abierto,
  onAbrir,
  onCambio,
}: {
  cliente: Cliente;
  tuneles: Tunel[];
  abierto: boolean;
  onAbrir: () => void;
  onCambio: () => void;
}): JSX.Element {
  const [prueba, setPrueba] = useState<Record<string, unknown> | null>(null);
  const [probando, setProbando] = useState<string | null>(null);
  const sinCif = cliente.companies.filter((c) => c.active && c.cif === null);

  async function probar(companyDb: string): Promise<void> {
    setProbando(companyDb);
    setPrueba(null);
    try {
      const r = await post<Record<string, unknown>>(`/admin/tenants/${cliente.slug}/companies/${companyDb}/test`);
      setPrueba(r);
    } catch (e) {
      setPrueba({ ok: false, error: e instanceof ApiError ? e.message : String(e) });
    } finally {
      setProbando(null);
      onCambio();
    }
  }

  return (
    <div className="tarjeta" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <button onClick={onAbrir} style={{ background: 'none', border: 0, padding: 0, font: 'inherit', cursor: 'pointer', textAlign: 'left' }}>
          <h2>{cliente.name}</h2>
          <div className="sutil">
            {cliente.slug}.mrp.ecinsapp.es · túnel <strong>{cliente.hubClient}</strong> · {cliente.slBaseUrl}
          </div>
        </button>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {!cliente.active ? <Dist tono="neutro" icono="—">Desactivado</Dist> : null}
          {cliente.companies.length === 0 ? (
            <Dist tono="critico" icono="■">Sin sociedades</Dist>
          ) : sinCif.length > 0 ? (
            <Dist tono="aviso" icono="▲">{sinCif.length} sin CIF</Dist>
          ) : (
            <Dist tono="bien" icono="✓">{cliente.companies.length} sociedad(es)</Dist>
          )}
          <button className="secundario" onClick={onAbrir}>{abierto ? 'Cerrar' : 'Abrir'}</button>
        </div>
      </div>

      {abierto ? (
        <div style={{ marginTop: 14 }}>
          <table>
            <thead>
              <tr>
                <th>company_db</th>
                <th>Nombre</th>
                <th>CIF que viaja</th>
                <th>Origen</th>
                <th>Usuario SAP</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {cliente.companies.map((s) => (
                <tr key={s.companyDb}>
                  <td className="mono"><strong>{s.companyDb}</strong>{s.isDefault ? <div className="sutil">principal</div> : null}</td>
                  <td>{s.name}</td>
                  <td className="mono">
                    {s.cif ?? <Dist tono="aviso" icono="▲">sin CIF</Dist>}
                    {/* Lo que SAP dijo y se descartó. Enseñarlo aquí evita
                        tener que abrir SAP para saber qué pasó. */}
                    {s.cifRejected ? <div className="sutil">SAP: «{s.cifSapRaw || '(nada)'}» — {s.cifRejected}</div> : null}
                    {s.cifSource === 'panel' && s.cifSapRaw ? <div className="sutil">SAP dice «{s.cifSapRaw}»</div> : null}
                  </td>
                  <td>
                    {s.cifSource === 'sap' ? <Dist tono="bien" icono="✓">De SAP</Dist> : s.cifSource === 'panel' ? <Dist tono="neutro" icono="✎">Tecleado</Dist> : <span className="sutil">—</span>}
                    {s.cifCheckedAt ? <div className="sutil">{new Date(s.cifCheckedAt).toLocaleString('es-ES')}</div> : null}
                  </td>
                  <td className="mono">{s.slUsername}</td>
                  <td>
                    <button className="secundario" disabled={probando === s.companyDb} onClick={() => void probar(s.companyDb)}>
                      {probando === s.companyDb ? 'Probando…' : 'Probar conexión'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {prueba ? <ResultadoPrueba prueba={prueba} /> : null}

          <NuevaSociedad slug={cliente.slug} onCreada={onCambio} />

          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            <NuevoUsuario slug={cliente.slug} />
            <button
              className="secundario"
              style={{ marginLeft: 'auto', borderColor: 'var(--critico)', color: 'var(--critico)' }}
              onClick={() => {
                if (!confirm(`¿Borrar el cliente «${cliente.slug}» y TODOS sus planes? No se puede deshacer.`)) return;
                void del(`/admin/tenants/${cliente.slug}`).then(onCambio);
              }}
            >
              Borrar cliente
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ResultadoPrueba({ prueba }: { prueba: Record<string, unknown> }): JSX.Element {
  const ok = prueba.ok === true;
  return (
    <div className={`aviso-caja ${ok ? '' : 'error'}`} style={{ marginTop: 12, borderColor: ok ? 'var(--bien)' : undefined }}>
      <div className="titulo">{ok ? 'Conexión correcta' : 'No se pudo conectar'}</div>
      {ok ? (
        <ul>
          <li>Sociedad en SAP: <strong>{String(prueba.nombreEnSap ?? '—')}</strong></li>
          <li>CIF que viaja: <strong>{String(prueba.cifQueViaja ?? 'ninguno')}</strong> ({String(prueba.origen ?? '—')})</li>
          <li>SAP devolvió: <span className="mono">{String(prueba.sapDevolvio ?? '(nada)')}</span></li>
          {prueba.aviso ? <li>{String(prueba.aviso)}</li> : null}
        </ul>
      ) : (
        <>
          <div>{String(prueba.error ?? '')}</div>
          {prueba.estado ? <div className="sutil" style={{ marginTop: 6 }}>{String(prueba.estado)}</div> : null}
        </>
      )}
    </div>
  );
}

function NuevaSociedad({ slug, onCreada }: { slug: string; onCreada: () => void }): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [db, setDb] = useState('');
  const [nombre, setNombre] = useState('');
  const [usuario, setUsuario] = useState('manager');
  const [clave, setClave] = useState('');
  const [cif, setCif] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pista, setPista] = useState<string | null>(null);

  if (!abierto) {
    return (
      <button className="secundario" style={{ marginTop: 12 }} onClick={() => setAbierto(true)}>
        Añadir sociedad
      </button>
    );
  }

  return (
    <div className="tarjeta" style={{ marginTop: 12, background: 'var(--superficie-2)' }}>
      <h3 style={{ marginBottom: 10 }}>Nueva sociedad</h3>
      <div className="rejilla dos">
        <label className="campo">company_db<input value={db} onChange={(e) => setDb(e.target.value)} placeholder="SBO_EMPRESA" /></label>
        <label className="campo">Nombre<input value={nombre} onChange={(e) => setNombre(e.target.value)} /></label>
        <label className="campo">Usuario de SAP<input value={usuario} onChange={(e) => setUsuario(e.target.value)} /></label>
        <label className="campo">Contraseña de SAP<input type="password" value={clave} onChange={(e) => setClave(e.target.value)} /></label>
        <label className="campo">
          CIF (déjalo vacío)
          <input value={cif} onChange={(e) => setCif(e.target.value)} placeholder="se lee de SAP" />
          <span className="sutil">
            El MRP lo lee del propio SAP al conectar con esta company_db. Solo se teclea aquí si en SAP
            no está bien puesto y no se puede arreglar allí ahora.
          </span>
        </label>
      </div>
      {error ? (
        <div className="aviso-caja error" style={{ marginTop: 10 }}>
          <div className="titulo">{error}</div>
          {pista ? <div className="sutil">{pista}</div> : null}
        </div>
      ) : null}
      <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
        <button
          className="primario"
          onClick={() => {
            setError(null);
            setPista(null);
            void post(`/admin/tenants/${slug}/companies`, {
              companyDb: db,
              name: nombre || db,
              slUsername: usuario,
              slPassword: clave,
              cifOverride: cif,
              isDefault: true,
              active: true,
            })
              .then(() => {
                setAbierto(false);
                setDb('');
                setClave('');
                setCif('');
                onCreada();
              })
              .catch((e: unknown) => {
                setError(e instanceof ApiError ? e.message : String(e));
                setPista(e instanceof ApiError ? e.hint : null);
              });
          }}
        >
          Guardar
        </button>
        <button className="secundario" onClick={() => setAbierto(false)}>Cancelar</button>
      </div>
    </div>
  );
}

function NuevoUsuario({ slug }: { slug: string }): JSX.Element {
  const [usuario, setUsuario] = useState('');
  const [nombre, setNombre] = useState('');
  const [rol, setRol] = useState('planificador');
  const [clave, setClave] = useState('');
  const [creado, setCreado] = useState<{ username: string; password: string | null; aviso: string } | null>(null);

  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label className="campo">Usuario<input value={usuario} onChange={(e) => setUsuario(e.target.value)} style={{ width: 130 }} /></label>
        <label className="campo">Nombre<input value={nombre} onChange={(e) => setNombre(e.target.value)} style={{ width: 160 }} /></label>
        <label className="campo">
          Perfil
          <select value={rol} onChange={(e) => setRol(e.target.value)}>
            <option value="director">Director</option>
            <option value="planificador">Planificador</option>
            <option value="consulta">Consulta</option>
          </select>
        </label>
        <label className="campo">
          Contraseña
          <input
            type="password"
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            placeholder="se genera sola"
            style={{ width: 150 }}
          />
        </label>
        <button
          className="secundario"
          onClick={() => {
            void post<{ username: string; password: string | null; aviso: string }>(
              `/admin/tenants/${slug}/users`,
              { username: usuario, name: nombre, role: rol, password: clave },
            ).then((r) => {
              setCreado(r);
              setClave('');
            });
          }}
        >
          Crear usuario
        </button>
      </div>
      {creado ? (
        <div className="aviso-caja" style={{ marginTop: 10 }}>
          <div className="titulo">Usuario {creado.username} creado</div>
          {creado.password ? (
            <div>
              Contraseña: <strong className="mono">{creado.password}</strong>
            </div>
          ) : null}
          <div className="sutil">{creado.aviso}</div>
        </div>
      ) : null}
    </div>
  );
}
